import { Box, ButtonBase, Typography } from "@mui/material";
import type { MouseEvent, ReactNode, Ref } from "react";
import { BADGE, CONTROL_HEIGHT, FONT_SIZE, INK, NEUTRAL, RADIUS, SURFACE } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

type ToolbarTriggerButtonProps = {
	/** Already-translated label. Shown unless `iconOnly`. */
	label: string;
	icon: ReactNode;
	/**
	 * Optional trailing count badge — the number of active sort keys, visible columns, and so on.
	 * Omit (or pass `undefined`) for no badge; a badge reading `0` is noise, so 0 is not rendered.
	 */
	count?: number;
	onClick: (event: MouseEvent<HTMLButtonElement>) => void;
	disabled?: boolean;
	/** Overrides the tooltip + `aria-label`, which otherwise both come from `label`. */
	description?: string;
	/** Drop the label and render a square icon button (a pill when it also carries a `count`). */
	iconOnly?: boolean;
	dataTestId?: string;
	/** The popover libraries anchor on the real DOM node, so this must reach the button element. */
	ref?: Ref<HTMLButtonElement>;
};

/**
 * The bordered, labelled button that opens a table's toolbar panel — Sort, Columns, and whatever
 * joins them next.
 *
 * Exists because these were each hand-rolled: Sort was a bare `IconButton` wrapping an unlabelled
 * glyph, Columns was a borderless `SystemButton` with text, and side by side they read as two
 * unrelated kinds of control at two different sizes. They do the same thing — open a panel that
 * changes what the table shows — so they share one button, pinned to `CONTROL_HEIGHT.STANDARD` so
 * they also line up with the search field on the same row.
 *
 * An unlabelled sort glyph floating above a grid was a measured defect on the Team page: people did
 * not know it was there, and could not tell it from a filter icon. Hence the word, not just the icon.
 */
export const ToolbarTriggerButton = ({
	label,
	icon,
	count,
	onClick,
	disabled = false,
	description,
	// DEFAULT: labelled — the whole point of the component. Callers opt out for cramped toolbars.
	iconOnly = false,
	dataTestId,
	ref,
}: ToolbarTriggerButtonProps) => {
	const title = description ?? label;
	// A badge is content, so it has to count toward the width: `iconOnly` is a square only while the
	// trigger really is icon-only. Pinned square with one, 44px of content sat in a 40px box and the
	// pill burst through the right border — every multi-key sort on every hand-rolled toolbar.
	const hasCount = count !== undefined && count > 0;

	return (
		<InHouseTooltip title={title}>
			<ButtonBase
				ref={ref}
				onClick={onClick}
				disabled={disabled}
				aria-label={title}
				data-testid={dataTestId}
				disableRipple
				sx={{
					// Explicit height rather than padding: the two triggers used to sit on different bases
					// and came out different sizes, and this is the row's alignment unit.
					height: CONTROL_HEIGHT.STANDARD,
					minHeight: CONTROL_HEIGHT.STANDARD,
					boxSizing: "border-box",
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 0.75,
					px: iconOnly ? (hasCount ? 0.75 : 0) : 1.25,
					width: iconOnly && !hasCount ? CONTROL_HEIGHT.STANDARD : "auto",
					// Square floor, so no variant comes out narrower than the plain icon one.
					minWidth: CONTROL_HEIGHT.STANDARD,
					flex: "none",
					borderRadius: `${RADIUS.CARD}px`,
					border: `1px solid ${NEUTRAL[300]}`,
					backgroundColor: SURFACE,
					color: disabled ? INK.DISABLED : INK.SECONDARY,
					whiteSpace: "nowrap",
					cursor: disabled ? "default" : "pointer",
					...(disabled
						? {}
						: {
								"@media (hover: hover) and (pointer: fine)": {
									"&:hover": { backgroundColor: NEUTRAL[50], borderColor: NEUTRAL[400] },
								},
							}),
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", "& svg": { fontSize: 18 } }}>{icon}</Box>
				{!iconOnly && <Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, lineHeight: 1 }}>{label}</Typography>}
				{hasCount && (
					<Box
						sx={{
							minWidth: BADGE.PILL_SIZE,
							height: BADGE.PILL_SIZE,
							px: 0.5,
							borderRadius: BADGE.PILL_RADIUS_FULL,
							bgcolor: disabled ? NEUTRAL[300] : "primary.main",
							color: disabled ? INK.DISABLED : "primary.contrastText",
							fontSize: FONT_SIZE.LABEL_MICRO,
							fontWeight: 700,
							display: "grid",
							placeItems: "center",
						}}
						data-testid={dataTestId ? `${dataTestId}-count` : undefined}
					>
						{count}
					</Box>
				)}
			</ButtonBase>
		</InHouseTooltip>
	);
};
